import debounce from 'lodash.debounce';
import { BREAKPOINT_LG } from 'utils/breakpoint';
import { closeAllMenuMobileNav } from './MobileNav';

interface NavbarTogglerProps {
    el: HTMLDivElement;
    props: {
        target: string;
    };
}

const DEBOUNCE_VALUE = 100;

const NavbarToggler = ({ el, props }: NavbarTogglerProps): void => {
    const { target } = props;

    const targetEl = document.querySelector(target) as HTMLDivElement | null;
    if (!targetEl) {
        return;
    }

    const updateMobileNavPosition = (): void => {
        const header = document.querySelector('.mobile-header');
        const acknowledgement = document.querySelector('.acknowledgement.active');
        const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
        if (acknowledgement) {
            if (targetEl) {
                targetEl.style.top = `${headerBottom}px`;
                targetEl.style.height = `calc(100vh - ${headerBottom}px)`;
            }
        }
    };

    // Initial position
    updateMobileNavPosition();

    // Update position on window resize
    window.addEventListener('resize', debounce(updateMobileNavPosition, DEBOUNCE_VALUE));

    const closeMobileNav = (): void => {
        targetEl.classList.remove('is-open');
        el.classList.remove('disabled');
        el.setAttribute('aria-expanded', 'false');
        closeAllMenuMobileNav({ el: targetEl });
        document.body.classList.remove('modal-opened');
    };

    const openMobileNav = (): void => {
        targetEl.classList.add('is-open');
        el.classList.remove('disabled');
        el.setAttribute('aria-expanded', 'true');
        document.body.classList.add('modal-opened');
        // Update position when nav is opened - necessary due to possible acknowledgement above mobile header
        updateMobileNavPosition();
    };

    // set default as close if no is-open class
    if (!targetEl.classList.contains('is-open')) {
        el.setAttribute('aria-expanded', 'false');
    }

    el.addEventListener('click', () => {
        if (el.classList.contains('disabled')) {
            return;
        }

        if (targetEl.classList.contains('is-open')) {
            closeMobileNav();
        } else {
            openMobileNav();
        }
    });

    const windowResizeCheck = (): void => {
        // if menu is opened while transition from mobile to desktop, close the mobile nav
        if (window.innerWidth > BREAKPOINT_LG && targetEl.classList.contains('is-open')) {
            closeMobileNav();
        }
    };

    windowResizeCheck();
    window.addEventListener('resize', debounce(windowResizeCheck, DEBOUNCE_VALUE));

    // Listen for acknowledgement close events to auto-close mobile-nav and reset nav position/height immediately
    const acknowledgement = document.querySelector('.acknowledgement');
    if (acknowledgement) {
        const observer = new MutationObserver(() => {
            if (!acknowledgement.classList.contains('active')) {
                // Remove is-open and reset nav immediately
                targetEl.classList.remove('is-open');
                el.classList.remove('disabled');
                el.setAttribute('aria-expanded', 'false');
                document.body.classList.remove('modal-opened');
                targetEl.style.top = '';
                targetEl.style.height = '';
            }
        });
        observer.observe(acknowledgement, { attributes: true, attributeFilter: ['class', 'style'] });
    }
};

export default NavbarToggler;
